import React, { useMemo, useState, useEffect } from "react";
import { SLOTS, STATUS_OPTIONS, STATUS_LABEL } from "../lib/slots";
import {
  flattenFinanceEntries, fmtEUR, fmtDateDE, fmtDateDEWeekday, todayIso, derivedDisplayStatus,
  saveFinanceFilters, financeYearStart,
} from "../lib/finance";
import { PlanningIcon, PLANNING_ICON_OPTIONS } from "../lib/planningIcons";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { Filter } from "lucide-react";

const PLANNING_ICON_LABEL = Object.fromEntries(
  PLANNING_ICON_OPTIONS.map((o) => [o.value, o.label]),
);

const VALID_PROJECT_STATUSES = new Set(["offer", "planned", "active", "waiting", "paused", "billed", "live", "completed", "archived"]);

// The Dashboard service-contract status filter is intentionally **not** persisted:
// every time the Dashboard is opened, it resets to the requested default
// (Live + Pausiert + Abgeschlossen). The user can change it while staying
// on the dashboard, but switching away and back will reset it again.
const SERVICE_STATUS_DEFAULT = ["live", "paused", "completed"];

function addDaysIso(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

export default function DashboardView({ projects, allProjects, onSelectProject, onNavigateToFinance }) {
  const today = todayIso();
  const allProj = Array.isArray(allProjects) && allProjects.length > 0 ? allProjects : projects;

  const [serviceStatusFilter, setServiceStatusFilter] = useState(() => [...SERVICE_STATUS_DEFAULT]);
  const [showSideMilestones, setShowSideMilestones] = useState(false);

  // VALID_PROJECT_STATUSES guards the filter array against stale/invalid values
  // that could survive future status renames.
  useEffect(() => {
    setServiceStatusFilter((prev) => prev.filter((v) => VALID_PROJECT_STATUSES.has(v)));
  }, []);

  const projectById = useMemo(() => {
    const m = new Map();
    for (const p of projects) m.set(p.id, p);
    for (const p of allProj) if (!m.has(p.id)) m.set(p.id, p);
    return m;
  }, [projects, allProj]);

  const flat = useMemo(() => flattenFinanceEntries(projects), [projects]);
  // Unfiltered payment stream — powers the top "Serviceverträge nächste 7 Tage"
  // card and the "Offene Zusatzrechnungen" reminder section which must both
  // ignore the global project-status filter.
  const flatAll = useMemo(() => flattenFinanceEntries(allProj), [allProj]);
  const flatWithDerived = useMemo(
    () => flat.map((e) => ({ ...e, status: derivedDisplayStatus(e, today) })),
    [flat, today],
  );

  // 1) Aktive Projekte
  const activeCount = useMemo(
    () => projects.filter((p) => p.status === "active").length,
    [projects],
  );

  // Upcoming main milestones (next 7) — only non-archived projects.
  const upcomingMilestones = useMemo(() => {
    return projects
      .filter((p) => p.status !== "archived" && p.milestone_date && p.milestone_date >= today)
      .sort((a, b) => a.milestone_date.localeCompare(b.milestone_date))
      .slice(0, 7);
  }, [projects, today]);

  const nextMilestone = upcomingMilestones[0] ?? null;

  // Merged list for the "Nächste Meilensteine" section. When the side-milestone
  // toggle is on, we keep exactly the next 7 main milestones and merge in all
  // visible planning notes (show_in_planner === true, within project bounds)
  // dated on or before the 7th main milestone. Chronological; main milestones
  // win ties on the same date.
  const milestoneListItems = useMemo(() => {
    const mains = upcomingMilestones.map((p) => ({
      kind: "main",
      key: `main-${p.id}`,
      date: p.milestone_date,
      project: p,
      title: p.milestone_title || "",
    }));
    if (!showSideMilestones || mains.length === 0) return mains;
    const horizon = mains[mains.length - 1].date;
    const notes = [];
    for (const p of projects) {
      if (p.status === "archived") continue;
      if (!Array.isArray(p.planning_notes)) continue;
      p.planning_notes.forEach((n, idx) => {
        if (!n?.show_in_planner || !n?.date) return;
        if (n.date < today || n.date > horizon) return;
        if (p.start_date && n.date < p.start_date) return;
        if (p.end_date && n.date > p.end_date) return;
        notes.push({
          kind: "note",
          key: `note-${p.id}-${idx}`,
          date: n.date,
          project: p,
          title: n.title || "",
          icon: n.planner_icon || "production",
        });
      });
    }
    return [...mains, ...notes].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      // Main wins ties.
      if (a.kind !== b.kind) return a.kind === "main" ? -1 : 1;
      return 0;
    });
  }, [upcomingMilestones, showSideMilestones, projects, today]);

  // 3) Heute fällig & überfällig — derived status = "due", or original due + due_date<=today
  const todayOverdue = useMemo(() => {
    let sum = 0;
    for (const e of flatWithDerived) {
      const amt = Number(e.amount || 0);
      if (!amt) continue;
      if (e.status === "due" && e.due_date && e.due_date <= today) sum += amt;
    }
    return sum;
  }, [flatWithDerived, today]);

  // 4) Offene Rechnungen bis heute — invoiced or due, with invoice_date <= today
  const openSum = useMemo(() => {
    let sum = 0;
    for (const e of flatWithDerived) {
      const amt = Number(e.amount || 0);
      if (!amt) continue;
      if (
        (e.status === "invoiced" || e.status === "due") &&
        e.invoice_date && e.invoice_date <= today
      ) sum += amt;
    }
    return sum;
  }, [flatWithDerived, today]);

  // 5) Noch zu fakturieren bis heute — planned with invoice_date <= today
  const toInvoiceUntilToday = useMemo(() => {
    let sum = 0;
    for (const e of flatWithDerived) {
      const amt = Number(e.amount || 0);
      if (!amt) continue;
      if (e.status === "planned" && e.invoice_date && e.invoice_date <= today) sum += amt;
    }
    return sum;
  }, [flatWithDerived, today]);

  // 6) Service contracts within next 7 days (top summary card). Intentionally
  // unfiltered — ignores the global project-status filter and the dashboard's
  // local serviceStatusFilter so the card always shows the complete upcoming
  // total. Completed projects are naturally included (only archived projects
  // are dropped by flattenFinanceEntries).
  const serviceNext7 = useMemo(() => {
    const horizon = addDaysIso(today, 7);
    let count = 0, sum = 0;
    for (const e of flatAll) {
      if (e.pay_type !== "service") continue;
      if (!e.invoice_date || e.invoice_date < today || e.invoice_date > horizon) continue;
      count += 1;
      sum += Number(e.amount || 0);
    }
    return { count, sum, horizon };
  }, [flatAll, today]);

  // 6b) Service contracts within next 30 days — only powers the list section
  // below. Same filter rules, longer horizon, so the freelancer sees what's
  // coming up beyond the very short 7-day window.
  const serviceNext30List = useMemo(() => {
    const horizon = addDaysIso(today, 30);
    return flatWithDerived
      .filter(
        (e) =>
          e.pay_type === "service" &&
          e.invoice_date &&
          e.invoice_date >= today &&
          e.invoice_date <= horizon,
      )
      .map((e) => ({ ...e, project_status: projectById.get(e.project_id)?.status || null }))
      .filter((e) =>
        serviceStatusFilter.length === 0
          ? true
          : e.project_status && serviceStatusFilter.includes(e.project_status),
      )
      .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date));
  }, [flatWithDerived, today, projectById, serviceStatusFilter]);

  const serviceNext30Horizon = useMemo(() => addDaysIso(today, 30), [today]);
  const tomorrowIso = useMemo(() => addDaysIso(today, 1), [today]);

  // "Offene Zusatzrechnungen" reminder — unfiltered by global/service filters.
  // Only shows Zusatzrechnungen (pay_type "extra") whose status is still
  // "planned" and whose invoice_date lies within [today-7d, today]. Once the
  // status changes or the date rolls past the 7-day window, the row disappears.
  const openExtras7 = useMemo(() => {
    const from = addDaysIso(today, -7);
    return flatAll
      .filter(
        (e) =>
          e.pay_type === "extra" &&
          e.status === "planned" &&
          e.invoice_date &&
          e.invoice_date >= from &&
          e.invoice_date <= today,
      )
      .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date));
  }, [flatAll, today]);

  const goFinance = (preset) => {
    if (!onNavigateToFinance) return;
    saveFinanceFilters({
      fromDate: financeYearStart(),
      until: today,
      upcomingStatus: preset.upcomingStatus,
      upcomingTypes: [],
    });
    onNavigateToFinance();
  };

  return (
    <div className="flex-1 h-full overflow-auto bg-slate-50" data-testid="dashboard-view">
      <div className="max-w-[1280px] mx-auto px-6 py-6 space-y-6">

        {/* 6 summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">

          {/* 1 */}
          <Card title="Aktive Projekte" testId="dash-card-active">
            <div className="text-3xl font-semibold tabular-nums text-slate-900"
                 style={{ fontFamily: "Manrope, ui-sans-serif" }}>
              {activeCount}
            </div>
          </Card>

          {/* 2 */}
          <Card
            title="Nächster Meilenstein"
            interactive={Boolean(nextMilestone)}
            onClick={() => nextMilestone && onSelectProject?.(nextMilestone)}
            testId="dash-card-next-milestone"
          >
            {nextMilestone ? (
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-sm border border-black/10 shrink-0"
                    style={{ backgroundColor: SLOTS[nextMilestone.slot - 1]?.color }}
                  />
                  <span className="text-sm font-medium text-slate-900 truncate">
                    {nextMilestone.name}
                  </span>
                </div>
                {nextMilestone.client && (
                  <div className="text-[11px] text-slate-500 truncate">{nextMilestone.client}</div>
                )}
                {nextMilestone.milestone_title && (
                  <div className="text-xs text-slate-700 truncate mt-1">{nextMilestone.milestone_title}</div>
                )}
                <div className="text-xs tabular-nums text-slate-500 mt-0.5">
                  {fmtDateDEWeekday(nextMilestone.milestone_date)}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400">—</div>
            )}
          </Card>

          {/* 3 */}
          <Card
            title="Heute fällig & überfällig"
            accent="text-rose-700"
            interactive
            onClick={() => goFinance({ upcomingStatus: "due" })}
            testId="dash-card-due"
          >
            <Amount value={todayOverdue} accent="text-rose-700" />
          </Card>

          {/* 4 */}
          <Card
            title="Offene Rechnungen bis heute"
            accent="text-sky-700"
            interactive
            onClick={() => goFinance({ upcomingStatus: "open" })}
            testId="dash-card-open"
          >
            <Amount value={openSum} accent="text-sky-700" />
          </Card>

          {/* 5 */}
          <Card
            title="Noch zu fakturieren bis heute"
            interactive
            onClick={() => goFinance({ upcomingStatus: "planned" })}
            testId="dash-card-to-invoice"
          >
            <Amount value={toInvoiceUntilToday} accent="text-slate-900" />
          </Card>

          {/* 6 */}
          <Card title="Serviceverträge nächste 7 Tage" testId="dash-card-service-7d">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums text-emerald-700"
                    style={{ fontFamily: "Manrope, ui-sans-serif" }}>
                {serviceNext7.count}
              </span>
              <span className="text-xs text-slate-500 tabular-nums">{fmtEUR(serviceNext7.sum)}</span>
            </div>
            <div className="text-[10px] text-slate-400 tabular-nums mt-1">
              bis {fmtDateDE(serviceNext7.horizon)}
            </div>
          </Card>
        </div>

        {/* Next Milestones */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 border-b border-slate-200 flex items-baseline justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium text-slate-900">Nächste Meilensteine</div>
            <div className="flex items-center gap-3">
              <label
                className="flex items-center gap-2 cursor-pointer select-none"
                data-testid="dash-side-milestones-toggle-label"
              >
                <Checkbox
                  checked={showSideMilestones}
                  onCheckedChange={(v) => setShowSideMilestones(Boolean(v))}
                  data-testid="dash-side-milestones-toggle"
                />
                <span className="text-xs text-slate-600">Nebenmeilensteine anzeigen</span>
              </label>
              <div className="text-xs text-slate-500 tabular-nums">{milestoneListItems.length}</div>
            </div>
          </div>
          {milestoneListItems.length === 0 ? (
            <div className="px-4 py-6 text-xs text-slate-400">Keine bevorstehenden Meilensteine.</div>
          ) : (
            <div>
              {milestoneListItems.map((it) => {
                const p = it.project;
                if (it.kind === "note") {
                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => onSelectProject?.(p)}
                      className="w-full grid items-center px-4 py-2 text-left hover:bg-slate-50 border-t border-slate-100 first:border-t-0 transition-colors bg-slate-50/50"
                      style={{ gridTemplateColumns: "138px minmax(0,1fr) minmax(0,1fr) minmax(0,160px)" }}
                      data-testid={`dash-milestone-note-row-${it.key}`}
                      title={PLANNING_ICON_LABEL[it.icon] || "Planungs-Notiz"}
                    >
                      <div className="text-xs tabular-nums text-slate-600">{fmtDateDEWeekday(it.date)}</div>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-700 truncate flex items-center gap-1.5">
                          <span
                            className="w-2 h-2 rounded-sm border border-black/10 shrink-0"
                            style={{ backgroundColor: SLOTS[p.slot - 1]?.color }}
                          />
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-white border border-slate-200 shrink-0">
                            <PlanningIcon icon={it.icon} size={10} color="#334155" />
                          </span>
                          <span className="truncate italic">{p.name}</span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 truncate italic">{it.title || "—"}</div>
                      <div className="text-xs text-slate-500 truncate">{p.client || ""}</div>
                    </button>
                  );
                }
                return (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => onSelectProject?.(p)}
                    className="w-full grid items-center px-4 py-2 text-left hover:bg-slate-50 border-t border-slate-100 first:border-t-0 transition-colors"
                    style={{ gridTemplateColumns: "138px minmax(0,1fr) minmax(0,1fr) minmax(0,160px)" }}
                    data-testid={`dash-milestone-row-${p.id}`}
                  >
                    <div className="text-xs tabular-nums text-slate-700">{fmtDateDEWeekday(it.date)}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-sm border border-black/10 shrink-0"
                          style={{ backgroundColor: SLOTS[p.slot - 1]?.color }}
                        />
                        {p.name}
                      </div>
                    </div>
                    <div className="text-xs text-slate-700 truncate">{it.title || "—"}</div>
                    <div className="text-xs text-slate-500 truncate">{p.client || ""}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Next Service Contracts */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 border-b border-slate-200 flex items-baseline justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium text-slate-900">
              Nächste Serviceverträge
              <span className="text-slate-400 font-normal ml-2 text-xs">
                · nächste 30 Tage · bis {fmtDateDE(serviceNext30Horizon)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="dash-service-status-filter-trigger"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span>
                      {serviceStatusFilter.length === 0
                        ? "Alle Status"
                        : serviceStatusFilter.length === 1
                          ? (STATUS_OPTIONS.find((s) => s.value === serviceStatusFilter[0])?.label ?? "")
                          : `${serviceStatusFilter.length} Status`}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2">
                  <div className="flex items-baseline justify-between px-1 py-1">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">
                      Projektstatus
                    </div>
                    {serviceStatusFilter.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setServiceStatusFilter([])}
                        className="text-[10px] text-slate-500 hover:text-slate-800"
                        data-testid="dash-service-status-filter-clear"
                      >
                        Alle anzeigen
                      </button>
                    )}
                  </div>
                  <div className="grid gap-1">
                    {STATUS_OPTIONS.map((s) => {
                      const checked = serviceStatusFilter.includes(s.value);
                      return (
                        <label
                          key={s.value}
                          className="flex items-center gap-2 px-1 py-1 rounded-sm hover:bg-slate-50 cursor-pointer"
                          data-testid={`dash-service-status-option-${s.value}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() =>
                              setServiceStatusFilter((prev) =>
                                checked ? prev.filter((v) => v !== s.value) : [...prev, s.value],
                              )
                            }
                          />
                          <span className="text-sm text-slate-700 flex-1">{s.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              <div className="text-xs text-slate-500 tabular-nums">{serviceNext30List.length}</div>
            </div>
          </div>
          {serviceNext30List.length === 0 ? (
            <div className="px-4 py-6 text-xs text-slate-400">
              Keine Servicevertrags-Rechnungen in den nächsten 30 Tagen.
            </div>
          ) : (
            <div>
              {serviceNext30List.map((e) => {
                const proj = projectById.get(e.project_id);
                const isToday = e.invoice_date === today;
                const isTomorrow = e.invoice_date === tomorrowIso;
                const dateClass = isToday
                  ? "text-rose-600 font-semibold"
                  : isTomorrow
                  ? "text-orange-600 font-semibold"
                  : "text-slate-700";
                return (
                  <button
                    key={e.key}
                    type="button"
                    onClick={() => proj && onSelectProject?.(proj)}
                    className="w-full grid items-center px-4 py-2 text-left hover:bg-slate-50 border-t border-slate-100 first:border-t-0 transition-colors"
                    style={{ gridTemplateColumns: "138px minmax(0,1.2fr) minmax(0,1fr) 120px minmax(0,140px)" }}
                    data-testid={`dash-service-row-${e.key}`}
                  >
                    <div className={`text-xs tabular-nums ${dateClass}`}>{fmtDateDEWeekday(e.invoice_date)}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-sm border border-black/10 shrink-0"
                          style={{ backgroundColor: SLOTS[e.slot - 1]?.color }}
                        />
                        {e.project_name}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{e.client || ""}</div>
                    <div className="text-[11px]">
                      <span className="inline-block px-1.5 py-0.5 rounded-sm border border-slate-200 bg-slate-50 text-slate-600">
                        {STATUS_LABEL[e.project_status] || "—"}
                      </span>
                    </div>
                    <div className="text-xs tabular-nums text-slate-700 text-right">{fmtEUR(e.amount)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Offene Zusatzrechnungen — unfiltered reminder */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 border-b border-slate-200 flex items-baseline justify-between">
            <div className="text-sm font-medium text-slate-900">
              Offene Zusatzrechnungen
              <span className="text-slate-400 font-normal ml-2 text-xs">
                · geplant · letzte 7 Tage
              </span>
            </div>
            <div className="text-xs text-slate-500 tabular-nums">{openExtras7.length}</div>
          </div>
          {openExtras7.length === 0 ? (
            <div className="px-4 py-6 text-xs text-slate-400">
              Keine offenen Zusatzrechnungen.
            </div>
          ) : (
            <div>
              {openExtras7.map((e) => {
                const proj = projectById.get(e.project_id);
                return (
                  <button
                    key={e.key}
                    type="button"
                    onClick={() => proj && onSelectProject?.(proj)}
                    className="w-full grid items-center px-4 py-2 text-left hover:bg-slate-50 border-t border-slate-100 first:border-t-0 transition-colors"
                    style={{ gridTemplateColumns: "138px minmax(0,1.2fr) minmax(0,1fr) 120px minmax(0,140px)" }}
                    data-testid={`dash-extra-row-${e.key}`}
                  >
                    <div className="text-xs tabular-nums text-slate-700">{fmtDateDEWeekday(e.invoice_date)}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-sm border border-black/10 shrink-0"
                          style={{ backgroundColor: SLOTS[e.slot - 1]?.color }}
                        />
                        {e.project_name}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{e.label}</div>
                    <div className="text-[11px]">
                      <span className="inline-block px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 tabular-nums tracking-wide">
                        Geplant
                      </span>
                    </div>
                    <div className="text-xs tabular-nums text-slate-700 text-right">{fmtEUR(e.amount)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, interactive = false, onClick, testId, accent }) {
  const base = "text-left bg-white border rounded-md p-4 transition-colors";
  const cls = interactive
    ? `${base} border-slate-200 hover:bg-slate-50 hover:border-slate-300 cursor-pointer`
    : `${base} border-slate-200`;
  const inner = (
    <>
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">{title}</div>
      {children}
    </>
  );
  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={cls} data-testid={testId}>
        {inner}
      </button>
    );
  }
  return <div className={cls} data-testid={testId}>{inner}</div>;
}

function Amount({ value, accent = "text-slate-900" }) {
  return (
    <div className={`text-2xl font-semibold tabular-nums ${accent}`}
         style={{ fontFamily: "Manrope, ui-sans-serif" }}>
      {fmtEUR(value)}
    </div>
  );
}
